import { DynamicMultiselect } from 'components/tables/list-table/filter-pieces/inputs/DynamicMultiSelect';
import { DynamicMultiselectFilterForm, SelectOption } from 'components/tables/list-table/ListTableTypes';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { useEffect, useState } from 'react';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { PortfolioScope, PortfolioScopePerson, PortfolioScopeState } from 'types/portfolio';
import { t } from 'utils/getTranslations';

const peopleToOptions = (people: PortfolioScopePerson[]): SelectOption[] =>
    people.map((person) => ({ value: person.id, label: person.name }));

const optionsToPeople = (options: SelectOption[]): PortfolioScopePerson[] =>
    options.map((option) => ({ id: Number(option.value), name: String(option.label) }));

interface PeoplePickerProps {
    value: PortfolioScopePerson[];
    onChange: (people: PortfolioScopePerson[]) => void;
}

// Async colleague multiselect for the "People" scope. Renders the very same filter-panel piece
// (DynamicMultiselect) the dynamic filters use, so search, lazy paging, the option checkboxes and
// the selected chips look and behave identically to a filter.
//
// That piece is a *controlled* multiselect: it owns its open state but expects `filter.selected` to
// mirror the live selection — its internal watchdog clears everything if `selected` is empty while
// options are checked, and a label-enrichment effect loops unless the emitted options are fed back
// verbatim. So we keep its emissions in local state, hand them straight back as `filter.selected`,
// and report each pick to the parent. The table no longer remounts on people changes (it refetches
// via MyPortfolioTable's scopeRefetchKey instead), so the dropdown stays open across picks.
const PeoplePicker = ({ value, onChange }: PeoplePickerProps) => {
    const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>(() => peopleToOptions(value));

    // Re-seed the local mirror when `value` changes from OUTSIDE this picker (URL restore, back/forward
    // navigation). The id-set guard means name-only changes (the picker's own label enrichment) keep
    // the current options instead of churning the controlled multiselect.
    useEffect(() => {
        const valueIdsKey = value.map((person) => person.id).join(',');
        setSelectedOptions((prev) =>
            prev.map((option) => Number(option.value)).join(',') === valueIdsKey ? prev : peopleToOptions(value)
        );
    }, [value]);

    const filter: DynamicMultiselectFilterForm = {
        type: 'dynamicmultiselect',
        selector: 'portfolioPeople',
        paginated: {
            fetch: useLazyGetEmployeesListWithFiltersQuery,
            searchField: 'fullName',
            getOptionIdentifier: 'id',
            getOptionLabel: 'name',
        },
        selected: selectedOptions,
    };

    return (
        <div className="flex w-[320px] min-w-0 shrink-0">
            <DynamicMultiselect
                filter={filter}
                quickFilters={{}}
                mainFilters={{}}
                mainFiltersInForm={[]}
                onChange={(options) => {
                    setSelectedOptions(options);
                    onChange(optionsToPeople(options));
                }}
                // The toolbar host has overflow:hidden; portal the dropdown so it isn't clipped.
                disablePortal={false}
                // Toolbar is tight: keep the field one fixed-height row (chips + "+N") rather than
                // growing tall and overlapping the view-mode toggle next to it.
                collapseTags
            />
        </div>
    );
};

interface Props {
    value: PortfolioScopeState;
    onChange: (next: PortfolioScopeState) => void;
}

// "View as" scope selector for the My Portfolio header toolbar. Visibility is gated by
// the parent on the PORTFOLIO_SCOPE_SELECTOR feature flag + PORTFOLIO.VIEW_OTHER permission, so
// this component assumes it is allowed to render when mounted. The People picker reuses the filter
// multiselect (see above); the scope picker is a plain select.
const PortfolioScopeSelector = ({ value, onChange }: Props) => {
    const scopeOptions = [
        { value: PortfolioScope.MY, label: t('portfolio.scope.my') },
        { value: PortfolioScope.USERS, label: t('portfolio.scope.people') },
        { value: PortfolioScope.TEAM, label: t('portfolio.scope.team') },
        { value: PortfolioScope.FIRM, label: t('portfolio.scope.firm') },
    ];

    const handleScopeChange = (scope: PortfolioScope) => {
        // Keep the chosen colleagues only while staying on (or returning to) the People scope.
        onChange({ scope, people: scope === PortfolioScope.USERS ? value.people : [] });
    };

    return (
        <div className="flex items-center gap-2">
            <Select
                items={scopeOptions}
                value={value.scope}
                onValueChange={(scope) => scope && handleScopeChange(scope)}
            >
                {/* While open, the trigger takes the focused-input border and halo, and its chevron flips. */}
                <SelectTrigger
                    size="sm"
                    aria-label={t('portfolio.scope.label')}
                    className="bg-paper data-popup-open:border-primary-focus data-popup-open:shadow-focus-ring text-xs data-popup-open:[&_svg]:rotate-180"
                >
                    <SelectValue />
                </SelectTrigger>
                {/* Drops below the trigger, left edges aligned, instead of the kit's default of opening over it, and is
                    only as wide as the trigger or its longest label. In classic the listbox inside it keeps 8px above
                    the first row and below the last, like develop's menu list; the new design keeps the kit's padding. */}
                <SelectContent
                    alignItemWithTrigger={false}
                    align="start"
                    className="not-new:[&>[role=listbox]]:py-2 w-auto min-w-(--anchor-width)"
                >
                    {/* 16px gutters and no check mark. The selected row is tinted with the primary at 8% at rest, 20%
                        keyboard-focused and 12% hovered, hover winning over focus. */}
                    {scopeOptions.map((option) => (
                        <SelectItem
                            key={option.value}
                            value={option.value}
                            className="data-selected:bg-primary/8 data-selected:hover:bg-primary/12 data-selected:focus-visible:bg-primary/20 data-selected:focus-visible:hover:bg-primary/12 px-4 [&_svg]:hidden"
                        >
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {value.scope === PortfolioScope.USERS ? (
                <PeoplePicker
                    value={value.people}
                    onChange={(people) => onChange({ scope: PortfolioScope.USERS, people })}
                />
            ) : null}
        </div>
    );
};

export default PortfolioScopeSelector;
